import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Calendar, LogOut, Search, AlertTriangle, Eye, ChevronDown, ChevronUp, BedDouble, Edit, CalendarPlus, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { format, addMonths, differenceInDays, parseISO } from "date-fns";
import { de } from "date-fns/locale";

const BRAND_COLOR = "#c11718";

export default function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [guests, setGuests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [allBeds, setAllBeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortBy, setSortBy] = useState("name"); // "name" or "date" or "room"
  const [formData, setFormData] = useState({
    guest_id: "",
    room_id: "",
    bed_id: "",
    check_in_date: "",
    check_out_date: "",
    monthly_rent: 350,
    deposit: 0,
    notes: "",
  });
  const [editData, setEditData] = useState({
    check_in_date: "",
    check_out_date: "",
    monthly_rent: 0,
    notes: "",
    room_id: "",
    bed_id: "",
  });
  const [extendMonths, setExtendMonths] = useState(1);
  const [checkoutData, setCheckoutData] = useState({
    problematic: false,
    reason: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (retryCount = 0) => {
    setLoadError(false);
    try {
      const [bookingsRes, guestsRes, roomsRes, bedsRes] = await Promise.all([
        api.get("/bookings"),
        api.get("/guests"),
        api.get("/rooms"),
        api.get("/beds"),
      ]);
      setBookings(bookingsRes.data);
      setGuests(guestsRes.data);
      setRooms(roomsRes.data);
      setAllBeds(bedsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      
      // Network hatası ve henüz 3 kez denemediyse tekrar dene
      if (!error.response && retryCount < 3) {
        console.log(`Retrying... attempt ${retryCount + 1}`);
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return fetchData(retryCount + 1);
      }
      
      if (error.response?.status === 401) {
        toast.error("Sitzung abgelaufen. Bitte melden Sie sich erneut an.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        setLoadError(true);
        toast.error("Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoomChange = async (roomId) => {
    setFormData((prev) => ({ ...prev, room_id: roomId, bed_id: "" }));
    const roomBeds = allBeds.filter((b) => b.room_id === roomId && b.is_available);
    setBeds(roomBeds);
    const room = rooms.find((r) => r.id === roomId);
    if (room) setFormData((prev) => ({ ...prev, monthly_rent: room.price_per_month }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bookings", {
        ...formData,
        check_in_date: new Date(formData.check_in_date).toISOString(),
        check_out_date: formData.check_out_date ? new Date(formData.check_out_date).toISOString() : null,
        monthly_rent: parseFloat(formData.monthly_rent),
        deposit: parseFloat(formData.deposit),
      });
      toast.success("Aufnahme erstellt");
      setDialogOpen(false);
      setFormData({ guest_id: "", room_id: "", bed_id: "", check_in_date: "", check_out_date: "", monthly_rent: 350, deposit: 0, notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    }
  };

  const handleCheckout = async () => {
    if (!selectedBooking) return;
    try {
      await api.post(`/bookings/${selectedBooking.id}/checkout`, checkoutData);
      toast.success("Auszug durchgeführt");
      setCheckoutDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Auszug");
    }
  };

  const openEditDialog = (booking) => {
    setSelectedBooking(booking);
    setEditData({
      check_in_date: booking.check_in_date ? booking.check_in_date.split('T')[0] : "",
      check_out_date: booking.check_out_date ? booking.check_out_date.split('T')[0] : "",
      monthly_rent: booking.monthly_rent || 0,
      notes: booking.notes || "",
      room_id: booking.room_id || "",
      bed_id: booking.bed_id || "",
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedBooking) return;
    try {
      await api.put(`/bookings/${selectedBooking.id}`, {
        check_in_date: editData.check_in_date,
        check_out_date: editData.check_out_date,
        monthly_rent: parseFloat(editData.monthly_rent),
        notes: editData.notes,
        room_id: editData.room_id || selectedBooking.room_id,
        bed_id: editData.bed_id || selectedBooking.bed_id,
      });
      toast.success("Aufnahme aktualisiert");
      setEditDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const openExtendDialog = (booking) => {
    setSelectedBooking(booking);
    setExtendMonths(1);
    setExtendDialogOpen(true);
  };

  const handleExtend = async () => {
    if (!selectedBooking) return;
    try {
      const currentEndDate = selectedBooking.check_out_date 
        ? parseISO(selectedBooking.check_out_date) 
        : new Date();
      const newEndDate = addMonths(currentEndDate, extendMonths);
      
      await api.put(`/bookings/${selectedBooking.id}`, {
        check_out_date: format(newEndDate, 'yyyy-MM-dd'),
      });
      toast.success(`Aufnahme um ${extendMonths} Monat(e) verlängert bis ${format(newEndDate, 'dd.MM.yyyy')}`);
      setExtendDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Verlängern");
    }
  };

  const getRemainingDays = (checkOutDate) => {
    if (!checkOutDate) return null;
    const days = differenceInDays(parseISO(checkOutDate), new Date());
    return days;
  };

  const filteredBookings = bookings.filter((b) => {
    const s = searchTerm.toLowerCase();
    return b.guest_name?.toLowerCase().includes(s) || b.room_number?.toLowerCase().includes(s);
  });

  // Sıralama fonksiyonu
  const sortBookings = (bookingsList) => {
    return [...bookingsList].sort((a, b) => {
      if (sortBy === "name") {
        return (a.guest_name || "").localeCompare(b.guest_name || "", 'de');
      } else if (sortBy === "date") {
        return new Date(b.check_in_date) - new Date(a.check_in_date);
      } else if (sortBy === "room") {
        return (a.room_number || "").localeCompare(b.room_number || "", 'de', { numeric: true });
      }
      return 0;
    });
  };

  const activeBookings = sortBookings(filteredBookings.filter((b) => b.status === "active"));
  const completedBookings = sortBookings(filteredBookings.filter((b) => b.status === "completed"));
  // Alphabetik sıralama ile available guests
  const availableGuests = guests
    .filter((g) => !g.current_bed_id)
    .sort((a, b) => {
      const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
      const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
      return nameA.localeCompare(nameB, 'de');
    });
  const availableRooms = rooms.filter((room) => allBeds.filter((b) => b.room_id === room.id && b.is_available).length > 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="bookings-page">
      {/* Error State with Retry Button */}
      {loadError && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">Daten konnten nicht geladen werden</p>
          </div>
          <Button onClick={() => { setLoading(true); fetchData(); }} variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
            <RefreshCw className="h-4 w-4 mr-2" />
            Erneut versuchen
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Aufnahme</h1>
          <p className="text-sm text-slate-600 mt-1">Bewohner-Aufnahmen verwalten</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-booking-btn">
              <Plus className="h-4 w-4 mr-2" />Neue Aufnahme
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Neue Aufnahme</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Bewohner *</Label>
                <Select value={formData.guest_id} onValueChange={(v) => setFormData((p) => ({ ...p, guest_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                  <SelectContent>
                    {availableGuests.length > 0 ? availableGuests.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.first_name} {g.last_name}</SelectItem>
                    )) : <div className="p-2 text-sm text-slate-500">Keine verfügbar</div>}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Zimmer *</Label>
                  <Select value={formData.room_id} onValueChange={handleRoomChange}>
                    <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent>
                      {availableRooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>Zi. {r.room_number}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bett *</Label>
                  <Select value={formData.bed_id} onValueChange={(v) => setFormData((p) => ({ ...p, bed_id: v }))} disabled={!formData.room_id}>
                    <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent>
                      {beds.map((b) => <SelectItem key={b.id} value={b.id}>{b.bed_number}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Einzug *</Label>
                  <Input type="date" value={formData.check_in_date} onChange={(e) => setFormData((p) => ({ ...p, check_in_date: e.target.value }))} required />
                </div>
                <div className="space-y-2">
                  <Label>Auszug</Label>
                  <Input type="date" value={formData.check_out_date} onChange={(e) => setFormData((p) => ({ ...p, check_out_date: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Miete (€) *</Label>
                  <Input type="number" step="0.01" value={formData.monthly_rent} onChange={(e) => setFormData((p) => ({ ...p, monthly_rent: e.target.value }))} required />
                </div>
                <div className="space-y-2">
                  <Label>Kaution (€)</Label>
                  <Input type="number" step="0.01" value={formData.deposit} onChange={(e) => setFormData((p) => ({ ...p, deposit: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Abbrechen</Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={!formData.guest_id || !formData.room_id || !formData.bed_id || !formData.check_in_date}>Erstellen</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Sortieren nach..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name (A-Z)</SelectItem>
            <SelectItem value="date">Einzug (Neueste)</SelectItem>
            <SelectItem value="room">Zimmer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active Bookings */}
      <Card className="border-slate-200">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
            Aktive ({activeBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {activeBookings.length > 0 ? (
            <div className="space-y-3">
              {activeBookings.map((b) => {
                const remainingDays = getRemainingDays(b.check_out_date);
                const isExpiringSoon = remainingDays !== null && remainingDays <= 30 && remainingDays >= 0;
                const isExpired = remainingDays !== null && remainingDays < 0;
                
                return (
                  <div key={b.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border ${isExpired ? 'bg-red-50 border-red-200' : isExpiringSoon ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`booking-row-${b.id}`}>
                    <div className="flex-1 min-w-0 mb-2 sm:mb-0">
                      <div className="font-medium text-sm truncate">{b.guest_name}</div>
                      <div className="text-xs text-slate-500">
                        Zi. {b.room_number} • {format(new Date(b.check_in_date), "dd.MM.yy", { locale: de })}
                        {b.check_out_date && ` - ${format(new Date(b.check_out_date), "dd.MM.yy", { locale: de })}`}
                      </div>
                      {remainingDays !== null && (
                        <div className={`text-xs mt-1 font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600' : 'text-slate-500'}`}>
                          {isExpired ? `${Math.abs(remainingDays)} Tage überfällig` : `${remainingDays} Tage verbleibend`}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
                      <span className="text-sm font-mono hidden sm:block mr-2">€{b.monthly_rent.toFixed(0)}</span>
                      
                      {/* Verlängern Button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => openExtendDialog(b)}
                        title="Verlängern"
                      >
                        <CalendarPlus className="h-4 w-4" />
                      </Button>
                      
                      {/* Bearbeiten Button */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(b)}
                        title="Bearbeiten"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      {/* Details Button */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setSelectedBooking(b); setDetailDialogOpen(true); }}
                        title="Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {/* Auszug Button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => { setSelectedBooking(b); setCheckoutData({ problematic: false, reason: "", notes: "" }); setCheckoutDialogOpen(true); }} 
                        data-testid={`checkout-${b.id}`}
                        title="Auszug"
                      >
                        <LogOut className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <BedDouble className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm">Keine aktiven Aufnahmen</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Bookings */}
      {completedBookings.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="p-4 cursor-pointer" onClick={() => setShowCompleted(!showCompleted)}>
            <CardTitle className="text-base text-slate-600 flex items-center justify-between" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span>Abgeschlossen ({completedBookings.length})</span>
              {showCompleted ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </CardTitle>
          </CardHeader>
          {showCompleted && (
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                {completedBookings.map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{b.guest_name}</div>
                      <div className="text-xs text-slate-500">Zi. {b.room_number}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {b.checkout_problematic && <Badge className="status-overdue text-xs"><AlertTriangle className="h-3 w-3" /></Badge>}
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedBooking(b); setDetailDialogOpen(true); }}><Eye className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-sm mx-4 sm:mx-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Details</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Bewohner</p><p className="font-medium text-sm">{selectedBooking.guest_name}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Zimmer</p><p className="font-medium text-sm">Zi. {selectedBooking.room_number}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Einzug</p><p className="font-medium text-sm">{format(new Date(selectedBooking.check_in_date), "dd.MM.yyyy", { locale: de })}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Auszug</p><p className="font-medium text-sm">{selectedBooking.check_out_date ? format(new Date(selectedBooking.check_out_date), "dd.MM.yyyy", { locale: de }) : "-"}</p></div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Miete</p><p className="font-medium font-mono">€{selectedBooking.monthly_rent.toFixed(2)}</p></div>
              {selectedBooking.checkout_problematic && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600 font-medium">Problematischer Auszug</p>
                  {selectedBooking.checkout_reason && <p className="text-sm text-red-800 mt-1">{selectedBooking.checkout_reason}</p>}
                  {selectedBooking.checkout_notes && <p className="text-sm text-red-800 mt-1">{selectedBooking.checkout_notes}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent className="max-w-sm mx-4 sm:mx-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Auszug</DialogTitle></DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Bewohner:</span><span className="font-medium">{selectedBooking.guest_name}</span></div>
                <div className="flex justify-between mt-1"><span className="text-slate-600">Zimmer:</span><span className="font-medium">Zi. {selectedBooking.room_number}</span></div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Checkbox id="problematic" checked={checkoutData.problematic} onCheckedChange={(c) => setCheckoutData((p) => ({ ...p, problematic: c }))} />
                <div>
                  <Label htmlFor="problematic" className="text-amber-800 font-medium cursor-pointer text-sm">Problematischer Auszug</Label>
                </div>
              </div>
              {checkoutData.problematic && (
                <Select value={checkoutData.reason} onValueChange={(v) => setCheckoutData((p) => ({ ...p, reason: v }))}>
                  <SelectTrigger><SelectValue placeholder="Grund auswählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hausordnung">Hausordnung</SelectItem>
                    <SelectItem value="zahlungsverzug">Zahlungsverzug</SelectItem>
                    <SelectItem value="gewalt">Gewalt</SelectItem>
                    <SelectItem value="drogen">Drogen</SelectItem>
                    <SelectItem value="diebstahl">Diebstahl</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Textarea value={checkoutData.notes} onChange={(e) => setCheckoutData((p) => ({ ...p, notes: e.target.value }))} rows={3} placeholder={checkoutData.problematic ? "Vermerk eingeben..." : "Bemerkungen (optional)"} />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCheckoutDialogOpen(false)} className="flex-1">Abbrechen</Button>
                <Button onClick={handleCheckout} className={`flex-1 ${checkoutData.problematic ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-900 hover:bg-slate-800"}`} disabled={checkoutData.problematic && !checkoutData.notes}>
                  Auszug
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Edit className="h-5 w-5" />
              Aufnahme bearbeiten
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Bewohner</p>
                <p className="font-medium">{selectedBooking.guest_name}</p>
              </div>
              
              {/* Zimmer/Bett Änderung */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Zimmer</Label>
                  <Select 
                    value={editData.room_id || selectedBooking.room_id} 
                    onValueChange={(v) => {
                      setEditData({...editData, room_id: v, bed_id: ""});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Zimmer wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map((room) => (
                        <SelectItem key={room.id} value={room.id}>
                          Zimmer {room.room_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bett</Label>
                  <Select 
                    value={editData.bed_id || selectedBooking.bed_id} 
                    onValueChange={(v) => setEditData({...editData, bed_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Bett wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {allBeds
                        .filter((b) => b.room_id === (editData.room_id || selectedBooking.room_id) && (b.is_available || b.id === selectedBooking.bed_id))
                        .map((bed) => (
                          <SelectItem key={bed.id} value={bed.id}>
                            {bed.bed_number} {bed.id === selectedBooking.bed_id ? "(aktuell)" : ""}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Einzugsdatum</Label>
                  <Input 
                    type="date" 
                    value={editData.check_in_date} 
                    onChange={(e) => setEditData({...editData, check_in_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Auszugsdatum</Label>
                  <Input 
                    type="date" 
                    value={editData.check_out_date} 
                    onChange={(e) => setEditData({...editData, check_out_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Monatsmiete (€)</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={editData.monthly_rent} 
                  onChange={(e) => setEditData({...editData, monthly_rent: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notizen</Label>
                <Textarea 
                  value={editData.notes} 
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                  Abbrechen
                </Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: BRAND_COLOR }}>
                  Speichern
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent className="max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <CalendarPlus className="h-5 w-5 text-emerald-600" />
              Aufnahme verlängern
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedBooking.guest_name}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Aktuelles Ende: {selectedBooking.check_out_date 
                    ? format(new Date(selectedBooking.check_out_date), "dd.MM.yyyy", { locale: de }) 
                    : "Nicht festgelegt"}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Verlängern um</Label>
                <Select value={String(extendMonths)} onValueChange={(v) => setExtendMonths(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Monat</SelectItem>
                    <SelectItem value="2">2 Monate</SelectItem>
                    <SelectItem value="3">3 Monate</SelectItem>
                    <SelectItem value="6">6 Monate</SelectItem>
                    <SelectItem value="12">12 Monate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-xs text-emerald-600">Neues Enddatum</p>
                <p className="font-medium text-emerald-800">
                  {selectedBooking.check_out_date 
                    ? format(addMonths(parseISO(selectedBooking.check_out_date), extendMonths), "dd.MM.yyyy", { locale: de })
                    : format(addMonths(new Date(), extendMonths), "dd.MM.yyyy", { locale: de })}
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setExtendDialogOpen(false)} className="flex-1">
                  Abbrechen
                </Button>
                <Button onClick={handleExtend} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Verlängern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
